/**
 * Tasks redesign — the TaskDrawer title shows the task's TEXT only; the @ person and # patient pills
 * inside task.content are kept aside and written back on every save (Nilay, 2026-10-07: "the
 * teammate's name already appears under ASSIGNED TO", and the patient under PATIENT).
 *
 * Why not hide the pills with CSS: the drawer saves the title's innerHTML, and taggedPatients is
 * rebuilt from the # badges in it (drawerModel.js contentPatch). A hidden pill could be deleted by
 * an unseen Backspace and silently unlink the patient. Splitting keeps the pills out of the editor
 * entirely, so the user cannot delete them by accident; the Patient field still adds / removes them.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE AND CLOCK-FREE (tasks-my-day-rules.test.js guards src/utils/tasks/).
 * The DOM is passed in (`doc`), so node-tests run it with JSDOM; the drawer passes `document`.
 *
 * Callers: src/pages/TaskList/components/TaskDrawer.vue. Test: cypress/node-tests/tasks-title-badges.test.js
 */

const BADGE_SELECTOR = '[data-teamio-employeeid], [data-teamio-patientid]';

/** The node to lift out for one badge: At's `[data-at-embedded]` wrapper, else a span holding only it. */
function liftNode(badge, box) {
  const embedded = badge.closest('[data-at-embedded]');
  if (embedded && embedded !== box && box.contains(embedded)) return embedded;
  let node = badge;
  while (node.parentElement && node.parentElement !== box && node.parentElement.tagName === 'SPAN'
    && node.parentElement.children.length === 1 && node.parentElement.textContent.trim() === node.textContent.trim()) {
    node = node.parentElement;
  }
  return node;
}

/** Visible text of an HTML string (tags and &nbsp; removed). */
function visibleText(html) {
  return String(html || '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .trim();
}

/** Trailing spaces / &nbsp; / <br> left behind where the pills were. */
function trimEnd(html) {
  return String(html || '').replace(/(?:\s|&nbsp;|&#160;|<br\s*\/?>)+$/i, '');
}

/**
 * Split a task title into the text the drawer edits and the pills it keeps aside.
 * @param {string} html  task.content
 * @param {Document} doc a DOM document (the drawer: window.document; tests: JSDOM)
 * @returns {{ text: string, badges: string[] }} badges = outerHTML of each lifted pill, in order.
 *   A title that is ONLY pills (no text of its own) is returned whole with badges [] — an empty
 *   editor would look broken and could not be saved.
 */
export function splitTitle(html, doc) {
  const src = typeof html === 'string' ? html : '';
  if (!src || !doc || typeof doc.createElement !== 'function') return { text: src, badges: [] };
  const box = doc.createElement('div');
  box.innerHTML = src;
  const found = Array.prototype.slice.call(box.querySelectorAll(BADGE_SELECTOR));
  if (!found.length) return { text: src, badges: [] };
  const nodes = [];
  found.forEach((b) => {
    const n = liftNode(b, box);
    if (nodes.indexOf(n) === -1 && !nodes.some(x => x.contains(n))) nodes.push(n);
  });
  const badges = nodes.map(n => n.outerHTML);
  nodes.forEach(n => n.parentNode && n.parentNode.removeChild(n));
  const text = trimEnd(box.innerHTML.replace(/(?:&nbsp;|\s){2,}/g, ' '));
  if (!visibleText(text)) return { text: src, badges: [] };
  return { text, badges };
}

/**
 * Put the kept pills back after the edited text (one space between). Pills the user typed into the
 * editor itself (a new @ or #) stay where they were typed, and a kept pill for the same person /
 * patient is then dropped (no duplicates).
 * @param {string} text   the editor's innerHTML
 * @param {string[]} badges from splitTitle
 * @returns {string} the content to save
 */
export function joinTitle(text, badges) {
  const t = typeof text === 'string' ? text : '';
  // A kept pill whose person / patient the user also typed into the text is not added twice.
  const list = (Array.isArray(badges) ? badges : []).filter((b) => {
    if (typeof b !== 'string' || !b) return false;
    const m = /data-teamio-(employeeid|patientid)="([^"]+)"/.exec(b);
    return !(m && t.indexOf('data-teamio-' + m[1] + '="' + m[2] + '"') !== -1);
  });
  if (!list.length) return t;
  return trimEnd(t) + ' ' + list.join(' ');
}
